<template>
  <!-- Add again -->
  <!-- <ChatButton /> -->

  <SettingsDialog v-model:visibility="dialogStates.settings" />
  <FeedbackDialog v-model:visibility="dialogStates.feedback" />
  <SmNotificationList />
  <BaseOnboardingDialog
    v-if="store.$state.showOnboardingDialog"
    v-model:visibility="store.$state.showOnboardingDialog" />
  <DebuggingModal v-if="store.state.showDebugModal" />
  <ApplicationReauthenticateDialog
    v-if="applicationReauthStore.reauthApplicationToHandle"
    v-model:visibility="store.$state.showReauthenticateDialog"
    :application="applicationReauthStore.reauthApplicationToHandle?.application"
    @authentication-completed="
      applicationReauthStore.clearReauthApplicationToHandle
    " />
  <ApplicationAuthFlowDialog
    v-if="applicationReauthStore.twoFactorAuthToHandle"
    v-model:visibility="store.$state.showAuthFlowDialog"
    :application-name="
      applicationReauthStore.twoFactorAuthToHandle.applicationName
    "
    :application-id="applicationReauthStore.twoFactorAuthToHandle.applicationId"
    :application-type="
      applicationReauthStore.twoFactorAuthToHandle.applicationType
    "
    :auth-type="applicationReauthStore.twoFactorAuthToHandle.authType"
    @authentication-completed="
      applicationReauthStore.clear2FaApplicationToHandle()
    " />
  <el-config-provider
    key="configProvider"
    :locale="language === PreferenceLanguages.DE ? el_de : el_en">
    <!-- Maintenance Banner -->
    <div v-if="showMaintenanceBanner" class="maintenance-banner">
      <div></div>
      <div>
        {{ i18n.t('maintenance-mode') }}
      </div>
      <div @click="showMaintenanceBanner = false">X</div>
    </div>

    <div
      :class="{
        'main-wrapper': true,
        'hide-on-mobile': showMobileNotSupported,
      }">
      <TheSidebar v-if="route.meta.layout === 'default'" class="no-print" />
      <router-view v-slot="{ Component, route: viewRoute }">
        <div class="flex h-full w-full flex-col">
          <ViewHeader
            v-if="
              viewRoute.meta.layout != 'empty' &&
              viewRoute.meta.showHeader !== false &&
              typeof viewRoute.name === 'string'
            "
            :name="viewRoute.name"
            :back-route="viewRoute.meta.backRoute as string"
            :title-key="viewRoute.meta.titleKey as string">
            <component
              :is="viewRoute.meta.customHeader"
              v-if="viewRoute.meta.customHeader"
              :key="route.matched[1]?.name ?? route.matched[1]?.path" />
          </ViewHeader>

          <transition :name="transition" mode="out-in">
            <div
              :key="route.matched[0]?.name ?? route.matched[0]?.path"
              class="transition-wrapper h-full max-h-screen w-full">
              <component :is="Component" />
            </div>
          </transition>
        </div>
      </router-view>
    </div>
    <div v-if="route.meta.requireLogin === true && isMobile" class="mobile">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="58"
        height="24"
        viewBox="0 0 58 24">
        <defs>
          <filter id="kqeay8qn4a">
            <feColorMatrix
              in="SourceGraphic"
              values="1 0 0 0 0
            0 1 0 0 0
            0 0 1 0 0
            0 0 0 1 0" />
          </filter>
        </defs>
        <g fill="none" fill-rule="evenodd">
          <g transform="translate(-20 -16) translate(20 16)">
            <path
              fill="#004f9f"
              fill-rule="nonzero"
              d="M29.7 23.5h-7.85v-4.448h7.225c1.875 0 3.2-.556 3.2-2.452 0-1.845-1.3-2.376-3.2-2.376h-1.15C23.7 14.047 21 11.798 21 7.324 21 2.825 23.85.5 28.3.5H36v4.448h-7.2c-1.875 0-3.125.556-3.125 2.401 0 1.846 1.225 2.452 3.125 2.452h1.275C34.3 9.978 37 12.076 37 16.651c0 4.347-2.85 6.849-7.3 6.849zM17 15.394V.5h-5.51v14.77c0 2.088-1.186 3.307-3.015 3.307-1.828 0-2.965-1.194-2.965-3.307V.5H0v14.894C0 20.342 3.805 23.5 8.475 23.5c4.646 0 8.525-3.158 8.525-8.106zM58 15.394V.5h-5.51v14.77c0 2.088-1.186 3.307-3.015 3.307-1.828 0-2.965-1.194-2.965-3.307V.5H41v14.894c0 4.948 3.805 8.106 8.475 8.106 4.646 0 8.525-3.158 8.525-8.106z" />
          </g>
        </g>
      </svg>
      <p>{{ i18n.t('mobileDisclaimer') }}</p>
    </div>
  </el-config-provider>
</template>

<script setup lang="ts">
  import {
    breakpointsTailwind,
    computedAsync,
    useBreakpoints,
    useColorMode,
    useIntervalFn,
    useMagicKeys,
  } from '@vueuse/core'
  import dayjs from 'dayjs'
  import duration from 'dayjs/plugin/duration'
  import isBetween from 'dayjs/plugin/isBetween'
  import el_de from 'element-plus/es/locale/lang/de'
  import el_en from 'element-plus/es/locale/lang/en'
  import { useI18n } from 'vue-i18n'
  import { useRoute, useRouter } from 'vue-router'

  import {
    OpenAPI,
    PreferenceAppearanceThemes,
    PreferenceLanguages,
  } from '@/client'
  import { dialogStates } from './utils/dialogManager'
  import { useGlobalStore } from './stores/globalStore'
  import { usePreferenceStore } from './stores/preferenceStore'
  import { useSessionStore } from './stores/sessionStore'
  import { useApplicationReauthStore } from './stores/applicationReauthStore'
  import { ApplicationStore } from './stores/application/application.store'
  import { OptimizationStore } from './stores/optimization/optimization.store'
  import {
    configureSQLite,
    db,
    ensureCompanyCacheScope,
    ensureSchemaVersion,
    updateAllCaches,
  } from './stores/database'
  import { logError } from './utils/errors'
  import { resolveMetaTitle } from './utils/metaTitle'

  dayjs.locale('de')
  dayjs.extend(isBetween)
  dayjs.extend(duration)

  const store = useGlobalStore()
  const applicationReauthStore = useApplicationReauthStore()

  const route = useRoute()
  const router = useRouter()
  const i18n = useI18n()

  const showMaintenanceBanner = ref(false)

  const sessionStore = useSessionStore()

  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isMobile = breakpoints.smaller('sm')

  OpenAPI.BASE = store.getIp
  OpenAPI.CREDENTIALS = 'include'
  OpenAPI.WITH_CREDENTIALS = true

  // This is false if the user is in mobile and is on a route which is not requiring a login. Otherwise it is true.
  // Example:
  // Mobile Dashboard => true
  // Mobile Login => false
  const showMobileNotSupported = computed(
    () => isMobile.value && route.meta.requireLogin
  )

  const previousRoute = ref(route)

  router.beforeEach((to, from, next) => {
    previousRoute.value = from
    next()
  })

  const transition = computed(() => {
    if (route.meta.noTransition || previousRoute.value.meta?.noTransition) {
      return 'none'
    }

    return 'fade'
  })

  // *************** User Preferences ***************
  // This should endsure, that the user preferences are loaded before the app is mounted
  // or if the user is not logged in, that the default preferences are loaded

  const preferenceStore = usePreferenceStore()

  watch(
    () => sessionStore.$state.currentCompany?.company_id,
    async (currentCompanyId) => {
      if (!currentCompanyId) {
        preferenceStore.resetPreferencesToDefaults(true)
        store.state.showOnboardingDialog = false
        return
      }

      await preferenceStore.ensureHydrated(true)
    },
    { immediate: true }
  )

  watch(
    () => preferenceStore.preferences?.currency_code,
    async () => {
      const currentCompanyId = sessionStore.$state.currentCompany?.company_id
      if (!currentCompanyId) return

      await Promise.all([
        ApplicationStore.updateApplicationCostsCacheFromApi(),
        OptimizationStore.updateSavingsCacheFromApi(),
      ])
    }
  )

  store.$subscribe((mutation) => {
    // Check if the jwt changed and user NOT logged out
    if (
      mutation.type === 'patch object' &&
      mutation.payload?.user?.jwt !== undefined &&
      mutation.payload.user.jwt !== null
    ) {
      // Load company info
      sessionStore.fetchCompanyInfo()
    }
  })

  // When preferences are loaded, or changed, apply them
  const mode = useColorMode()

  preferenceStore.$subscribe((_, state) => {
    const { appearance, language } = state.preferences
    mode.value =
      appearance === PreferenceAppearanceThemes.LIGHT_THEME ? 'light' : 'dark'

    if (language) {
      i18n.locale.value = language
    }
  })

  const language = computedAsync(async () => {
    const currentLanguage = preferenceStore.preferences?.language

    if (currentLanguage) {
      i18n.locale.value = currentLanguage
    }
    return currentLanguage
  })

  // This watch sets the document title
  watch(
    () => route.fullPath,
    async (_, __, onInvalidate) => {
      let cancelled = false
      onInvalidate(() => {
        cancelled = true
      })

      const title = await resolveMetaTitle(route)
      if (cancelled) return

      document.title = title
    },
    { immediate: true }
  )

  // This watch checks if the onboarding dialog needs to be shown
  // We only want to trigger this when the app is initialized or when the user logs in
  // not on every single route change, to avoid reappearance bugs.
  watch(
    () => [
      route.meta.requireLogin,
      preferenceStore.isHydrated,
      sessionStore.$state.currentCompany?.company_id,
    ],
    async ([requireLogin, isHydrated, currentCompanyId]) => {
      if (requireLogin !== true || !isHydrated || !currentCompanyId) {
        if (requireLogin !== true) {
          store.state.showOnboardingDialog = false
        }
        return
      }

      const prefs = preferenceStore.preferences
      if (prefs.show_onboarding_dialog) {
        store.state.showOnboardingDialog = true
      } else {
        store.state.showOnboardingDialog = false
      }
    },
    { immediate: true }
  )

  onMounted(async () => {
    sessionStore.fetchCompanyInfo()
    sessionStore.checkSession()
  })

  const isSwitchingCompany = ref(false)
  const isCacheSyncRunning = ref(false)

  async function syncAllCachesSafely() {
    if (isSwitchingCompany.value || isCacheSyncRunning.value) {
      return
    }

    const currentCompany = sessionStore.$state.currentCompany
    if (!currentCompany?.company_id) {
      return
    }

    isCacheSyncRunning.value = true

    try {
      OpenAPI.BASE = store.getIp
      OpenAPI.TOKEN = currentCompany.token

      await ensureCompanyCacheScope(currentCompany.company_id)
      await updateAllCaches()
    } catch (error) {
      logError(
        error,
        'Periodic cache sync failed',
        {
          companyId: currentCompany.company_id,
        },
        { op: 'cache.periodicSync' }
      )
    } finally {
      isCacheSyncRunning.value = false
    }
  }

  // Update Caches
  const { pause, resume } = useIntervalFn(syncAllCachesSafely, 30_000, {
    immediate: false,
  })

  watch(
    () => sessionStore.$state.currentCompany?.company_id,
    async (currentCompany) => {
      pause()

      if (!currentCompany) {
        isSwitchingCompany.value = false
        return
      }

      isSwitchingCompany.value = true

      try {
        const activeCompany = sessionStore.$state.currentCompany
        if (!activeCompany) {
          return
        }

        OpenAPI.BASE = store.getIp
        OpenAPI.TOKEN = activeCompany.token

        await configureSQLite(db)
        await ensureSchemaVersion()
        await ensureCompanyCacheScope(currentCompany)
        await updateAllCaches(true)
        resume()
      } catch (error) {
        logError(
          error,
          'Company cache bootstrap failed',
          {
            companyId: currentCompany,
          },
          { op: 'cache.companyBootstrap' }
        )
      } finally {
        isSwitchingCompany.value = false
      }
    },
    { immediate: true }
  )

  // Debugging Modals
  const { j, o, s } = useMagicKeys()
  watchEffect(() => {
    if (j.value && o.value && s.value) {
      store.state.showDebugModal = !store.state.showDebugModal
    }
  })
</script>

<style scoped>
  /* If the User uses an mobile enddevice */
  .hide-on-mobile {
    display: none;
  }

  .mobile {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    justify-content: center;
    align-items: center;
  }

  .mobile-icon {
    width: 80%;
  }

  .maintenance-banner {
    background-color: var(--sm-orange);
    color: white;
    padding: 10px;
    text-align: center;
    font-size: 14px;
    font-weight: 500;

    display: flex;
    justify-content: space-between;

    position: absolute;
    top: 0;
    left: 0;
    right: 0;

    z-index: 10000;
  }
</style>

<style>
  @media print {
    .no-print {
      display: none !important;
    }

    .print-only {
      display: block;
    }
  }
</style>
